import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { StoryImportCreditStep } from "@/components/story-import/story-import-credit-step";
import { StoryImportNotice } from "@/components/story-import/story-import-notice";
import { StoryImportProcessingStep } from "@/components/story-import/story-import-processing-step";
import { StoryImportStepper } from "@/components/story-import/story-import-stepper";
import { StoryImportUploadStep } from "@/components/story-import/story-import-upload-step";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { getStoryImportClientConfig } from "@/features/story-import/api/storyImportClient";
import type { StoryImportStep } from "@/features/story-import/story-import.types";
import {
  buildProjectStoryImportReviewRoute,
} from "@/features/story-import/storyImportMappers";
import { useStoryImportStore } from "@/stores/storyImportStore";

export function StoryImportPage() {
  const { t } = useTranslation();
  const clientConfig = getStoryImportClientConfig();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [suppressProjectResume, setSuppressProjectResume] = useState(false);
  const activeStep = useStoryImportStore((state) => state.activeStep);
  const cancelProcessing = useStoryImportStore((state) => state.cancelProcessing);
  const cancelDraft = useStoryImportStore((state) => state.cancelDraft);
  const clearNotice = useStoryImportStore((state) => state.clearNotice);
  const continueProcessing = useStoryImportStore((state) => state.continueProcessing);
  const currentJob = useStoryImportStore((state) => state.currentJob);
  const draft = useStoryImportStore((state) => state.draft);
  const hydrateJob = useStoryImportStore((state) => state.hydrateJob);
  const hydrateLatestPendingJob = useStoryImportStore((state) => state.hydrateLatestPendingJob);
  const isCanceling = useStoryImportStore((state) => state.isCanceling);
  const isRefreshing = useStoryImportStore((state) => state.isRefreshing);
  const isRetrying = useStoryImportStore((state) => state.isRetrying);
  const isStarting = useStoryImportStore((state) => state.isStarting);
  const isUploading = useStoryImportStore((state) => state.isUploading);
  const loadQuote = useStoryImportStore((state) => state.loadQuote);
  const notice = useStoryImportStore((state) => state.notice);
  const quote = useStoryImportStore((state) => state.quote);
  const reset = useStoryImportStore((state) => state.reset);
  const retryProcessing = useStoryImportStore((state) => state.retryProcessing);
  const selectFile = useStoryImportStore((state) => state.selectFile);
  const startProcessing = useStoryImportStore((state) => state.startProcessing);
  const jobId = searchParams.get("jobId");

  useEffect(() => {
    function isFileDrag(event: DragEvent) {
      return Array.from(event.dataTransfer?.types ?? []).includes("Files");
    }

    function preventWindowFileDrop(event: DragEvent) {
      if (!isFileDrag(event)) {
        return;
      }

      event.preventDefault();
    }

    window.addEventListener("dragover", preventWindowFileDrop);
    window.addEventListener("drop", preventWindowFileDrop);

    return () => {
      window.removeEventListener("dragover", preventWindowFileDrop);
      window.removeEventListener("drop", preventWindowFileDrop);
    };
  }, []);

  useEffect(() => {
    if (!quote) {
      void loadQuote().catch(() => undefined);
    }
  }, [loadQuote, quote]);

  useEffect(() => {
    let cancelled = false;

    async function hydrateEntryPoint() {
      if (jobId) {
        const job = await hydrateJob(jobId);

        if (cancelled) {
          return;
        }

        if (!job) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete("jobId");
          setSearchParams(nextParams, { replace: true });
          return;
        }

        if (job.projectId && job.status === "review_ready") {
          navigate(buildProjectStoryImportReviewRoute(job.projectId, job.jobId), {
            replace: true,
          });
        }

        return;
      }

      if (!projectId && !jobId && currentJob?.jobId) {
        if (currentJob.projectId && currentJob.status === "review_ready") {
          navigate(buildProjectStoryImportReviewRoute(currentJob.projectId, currentJob.jobId), {
            replace: true,
          });
        }
        return;
      }

      if (draft || currentJob?.jobId) {
        return;
      }

      if (projectId) {
        return;
      }

      const latestJob = await hydrateLatestPendingJob();

      if (cancelled || !latestJob) {
        return;
      }

      if (latestJob.projectId && latestJob.status === "review_ready") {
        navigate(buildProjectStoryImportReviewRoute(latestJob.projectId, latestJob.jobId), {
          replace: true,
        });
        return;
      }

      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("jobId", latestJob.jobId);
      setSearchParams(nextParams, { replace: true });
    }

    void hydrateEntryPoint();

    return () => {
      cancelled = true;
    };
  }, [
    currentJob?.jobId,
    draft,
    hydrateJob,
    hydrateLatestPendingJob,
    jobId,
    navigate,
    projectId,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (
      !currentJob?.jobId
      || currentJob.projectId
      || currentJob.status === "failed"
      || jobId === currentJob.jobId
    ) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("jobId", currentJob.jobId);
    setSearchParams(nextParams, { replace: true });
  }, [currentJob?.jobId, currentJob?.projectId, jobId, searchParams, setSearchParams]);

  useEffect(() => {
    if (
      jobId
      && currentJob?.jobId === jobId
      && currentJob.projectId
      && currentJob.status === "review_ready"
    ) {
      navigate(buildProjectStoryImportReviewRoute(currentJob.projectId, currentJob.jobId), {
        replace: true,
      });
    }
  }, [currentJob?.jobId, currentJob?.projectId, currentJob?.status, jobId, navigate]);

  useEffect(() => {
    if (currentJob?.status !== "failed" || jobId !== currentJob.jobId) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("jobId");
    setSearchParams(nextParams, { replace: true });
  }, [currentJob?.jobId, currentJob?.status, jobId, searchParams, setSearchParams]);

  useEffect(() => {
    if (
      !currentJob?.jobId
      || (currentJob.status !== "queued"
        && currentJob.status !== "pending"
        && currentJob.status !== "processing"
        && currentJob.status !== "canceling")
    ) {
      return;
    }

    void continueProcessing();
    const pollMs = clientConfig.progressPollMs;
    const intervalId = window.setInterval(() => {
      void continueProcessing();
      void hydrateJob(currentJob.jobId);
    }, pollMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [clientConfig.progressPollMs, continueProcessing, currentJob?.jobId, currentJob?.status, hydrateJob]);

  const labels = useMemo<Record<StoryImportStep, string>>(
    () => ({
      "credit-estimate": t("storyImport.steps.creditEstimate"),
      processing: t("storyImport.steps.processing"),
      upload: t("storyImport.steps.upload"),
    }),
    [t],
  );

  const tierLocked = quote ? quote.tier !== "author" : false;
  const currentJobProjectId = currentJob?.projectId ?? null;
  const resolvedActiveStep = useMemo<StoryImportStep>(() => {
    if (suppressProjectResume && projectId && !jobId && !draft) {
      return "upload";
    }

    return activeStep;
  }, [activeStep, draft, jobId, projectId, suppressProjectResume]);

  useEffect(() => {
    if (jobId || draft || activeStep === "credit-estimate") {
      setSuppressProjectResume(false);
    }
  }, [activeStep, draft, jobId]);

  const noticeAction = useMemo(() => {
    if (!notice || resolvedActiveStep !== "processing" || !currentJob) {
      return null;
    }

    if (currentJob.status === "failed") {
      return {
        actionLabel: "storyImport.actions.retry",
        onAction: () => {
          void retryProcessing().catch(() => undefined);
        },
      };
    }

    if (
      currentJobProjectId
      && currentJob.status === "review_ready"
    ) {
      return {
        actionLabel: "storyImport.actions.openReview",
        onAction: () => {
          navigate(buildProjectStoryImportReviewRoute(currentJobProjectId, currentJob.jobId));
        },
      };
    }

    return null;
  }, [currentJob, currentJobProjectId, navigate, notice, resolvedActiveStep, retryProcessing]);

  function clearJobParams() {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[2.25rem] border border-white/70 bg-white/72 p-6 shadow-panel">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-clay">
              {t("storyImport.page.eyebrow")}
            </p>
            <div className="space-y-2">
              <h1 className="font-display text-5xl text-ink">{t("storyImport.page.title")}</h1>
              <p className="max-w-3xl text-base leading-7 text-ink/70">
                {t("storyImport.page.body")}
              </p>
            </div>
          </div>

          {projectId ? (
            <Button asChild type="button" variant="outline">
              <Link to={`/projects/${projectId}`}>
                {t("storyImport.page.backToProject")}
              </Link>
            </Button>
          ) : (
            <Button asChild type="button" variant="outline">
              <Link to="/account/billing">
                <Sparkles className="h-4 w-4" />
                {t("storyImport.page.billingCta")}
              </Link>
            </Button>
          )}
        </div>
      </section>

      {tierLocked ? (
        <Card>
          <CardContent className="flex flex-col gap-4 p-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <p className="text-lg font-semibold text-foreground">
                {t("storyImport.tierLock.title")}
              </p>
              <p className="max-w-3xl text-sm text-muted-foreground">
                {t("storyImport.tierLock.body")}
              </p>
            </div>
            <Button asChild type="button">
              <Link to="/account/billing">{t("storyImport.tierLock.cta")}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {notice ? (
        <StoryImportNotice
          actionLabel={noticeAction?.actionLabel}
          notice={notice}
          onAction={noticeAction?.onAction}
          onDismiss={clearNotice}
        />
      ) : null}

      <StoryImportStepper currentStep={resolvedActiveStep} labels={labels} />

      {resolvedActiveStep === "upload" ? (
        <StoryImportUploadStep
          disabled={tierLocked}
          draft={draft}
          fileSizeLimitBytes={clientConfig.fileSizeLimitBytes}
          maxPageCount={clientConfig.maxPageCount}
          isUploading={isUploading}
          onSelectFile={(file) => {
            setSuppressProjectResume(false);
            void selectFile(file, projectId ?? null).catch(() => undefined);
          }}
        />
      ) : null}

      {resolvedActiveStep === "credit-estimate" ? (
        <StoryImportCreditStep
          draft={draft}
          isStarting={isStarting}
          onCancel={() => {
            void cancelDraft()
              .then(() => {
                clearJobParams();
              })
              .catch(() => undefined);
          }}
          onConfirm={() => {
            void startProcessing()
              .then((job) => {
                const nextParams = new URLSearchParams(searchParams);
                nextParams.set("jobId", job.jobId);
                setSearchParams(nextParams, { replace: true });
              })
              .catch(() => undefined);
          }}
          quote={quote}
        />
      ) : null}

      {resolvedActiveStep === "processing" ? (
        <StoryImportProcessingStep
          isCanceling={isCanceling}
          isRefreshing={isRefreshing}
          job={currentJob}
          onCancel={
            currentJob?.status === "queued"
              || currentJob?.status === "pending"
              || currentJob?.status === "processing"
              ? () => {
                  void cancelProcessing().catch(() => undefined);
                }
              : undefined
          }
          onOpenReview={
            currentJob
              && currentJobProjectId
              && currentJob.status === "review_ready"
              ? () => {
                  navigate(
                    buildProjectStoryImportReviewRoute(
                      currentJobProjectId,
                      currentJob.jobId,
                    ),
                  );
                }
              : undefined
          }
          onRetry={
            currentJob?.status === "failed"
              ? () => {
                  void retryProcessing().catch(() => undefined);
                }
              : undefined
          }
        />
      ) : null}

      {resolvedActiveStep === "processing" ? (
        <div className="flex justify-end">
          <Button
            disabled={isRetrying || isCanceling}
            onClick={() => {
              setSuppressProjectResume(true);
              reset();
              clearJobParams();
            }}
            type="button"
            variant="ghost"
          >
            {t("storyImport.actions.clearLocalState")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
